import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isValidHref, isInternalLink } from '../../assets/utils/sidebar/sidebarUtils';

/**
 * 사이드바 서브메뉴 - 아코디언 스타일
 * 원본: sub-sidebar-menu.html
 *
 * data 구조: { title, menu[], activeHref }
 * menu 구조: [{ label, href?, children[] }]
 */
const SubSidebarMenu = ({ data, position = 'left' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const rootRef = useRef(null);
  const accordionRef = useRef(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [sidebarData, setSidebarData] = useState(null);
  const uidRef = useRef(`acc-${Math.random().toString(36).slice(2, 8)}`);

  // 데이터 초기화 및 location.pathname 변경 감지
  useEffect(() => {
    // window.SidebarMenuData를 우선적으로 사용하되, 없으면 data prop 사용
    const sidebarMenuData = window.SidebarMenuData || data || { title: '', menu: [], activeHref: '' };

    // location.pathname이 변경되었을 때 activeHref 업데이트
    const updatedData = {
      ...sidebarMenuData,
      activeHref: location.pathname
    };

    setSidebarData(updatedData);
  }, [data, location.pathname]);

  // 초기 확장 상태 설정 (active 항목이 있는 그룹은 자동 확장)
  useEffect(() => {
    if (!sidebarData) return;

    // location.pathname을 우선적으로 사용하여 항상 최신 경로 반영
    const activeHref = location.pathname;
    const groups = Array.isArray(sidebarData.menu) ? sidebarData.menu : [];
    const newExpandedItems = new Set();

    const hasActive = (group) => {
      const children = Array.isArray(group?.children) ? group.children : [];
      return children.some((child) => child && child.href === activeHref);
    };

    groups.forEach((group, index) => {
      if (hasActive(group)) {
        newExpandedItems.add(index);
      }
    });

    setExpandedItems(newExpandedItems);
  }, [sidebarData, location.pathname]);

  // 아코디언 토글
  const toggleAccordion = (index) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // 링크 클릭 핸들러
  const handleLinkClick = (e, href) => {
    if (!isValidHref(href)) {
      e.preventDefault();
      return;
    }

    if (isInternalLink(href)) {
      e.preventDefault();
      e.stopPropagation();

      // 현재 활성화된 메뉴 항목을 다시 클릭한 경우 업무화면 영역만 새로고침
      const currentPath = location.pathname;
      if (href === currentPath) {
        // 전역 함수를 통해 페이지 데이터만 다시 로드
        if (typeof window.reloadPageData === 'function') {
          window.reloadPageData();
        }
        return;
      }

      navigate(href);
    }
    // 외부 링크는 기본 동작 허용
  };

  // 사이드바 토글 초기화
  useEffect(() => {
    if (!rootRef.current) return;

    const sidebarRoot = rootRef.current.querySelector('.sidebar-root') || rootRef.current;

    if (typeof window.SidebarMenuCommon !== 'undefined' &&
        typeof window.SidebarMenuCommon.initSidebarToggle === 'function') {
      window.SidebarMenuCommon.initSidebarToggle(sidebarRoot);
    } else if (typeof window.initSidebarToggle === 'function') {
      window.initSidebarToggle(sidebarRoot);
    }

    if (typeof window.initSidebarScrollOffset === 'function') {
      window.initSidebarScrollOffset();
    }

    if (typeof window.AOS !== 'undefined') {
      requestAnimationFrame(() => {
        window.AOS.refreshHard();
      });
    }
  }, [sidebarData]);

  if (!sidebarData) return null;

  const groups = Array.isArray(sidebarData.menu) ? sidebarData.menu : [];
  // location.pathname을 우선적으로 사용하여 항상 최신 경로 반영
  const activeHref = location.pathname;
  const uid = uidRef.current;

  return (
    <div
      ref={rootRef}
      className="sidebar-menu"
      data-aos={position === 'right' ? 'fade-left' : 'fade-right'}
      data-aos-delay="100"
    >
      <div className="sidebar-root">
        <div className="sidebar-title-wrapper">
          <h5 className="sidebar-title">
            <span className="sidebar-title-bar"></span>
            <span className="sidebar-title-text">{sidebarData.title || '섹션 제목'}</span>
            <button
              type="button"
              className="sidebar-toggle-btn"
              id="sidebar-toggle-btn"
              aria-label="사이드바 접기/펼치기"
            >
              <i className="ti-angle-left sidebar-toggle-icon"></i>
            </button>
          </h5>
        </div>
        <div className="accordion" ref={accordionRef} id={uidRef.current}>
          {groups.map((group, index) => {
            const groupId = `${uid}-${index}`;
            const expanded = expandedItems.has(index);
            const children = Array.isArray(group?.children) ? group.children : [];
            const hasChildren = children.length > 0;

            const groupHref = group?.href;
            const hasValidGroupHref = isValidHref(groupHref);
            // 자식이 active인 경우에는 부모까지 active 처리하지 않음(중복 하이라이트 방지)
            const isChildActiveInGroup = hasChildren && children.some((c) => isValidHref(c?.href) && c.href === activeHref);
            const isGroupActive = hasValidGroupHref && groupHref === activeHref && !isChildActiveInGroup;

            return (
              <div key={index} className="accordion-item">
                <h2 className="accordion-header" id={`heading-${groupId}`}>
                  {hasValidGroupHref ? (
                    <a
                      href={groupHref}
                      className={`accordion-button ${!hasChildren ? 'no-arrow' : ''} ${isGroupActive ? 'active' : ''} ${expanded ? '' : 'collapsed'}`}
                      onClick={(e) => {
                        if (isInternalLink(groupHref)) {
                          handleLinkClick(e, groupHref);
                        }
                        if (hasChildren) {
                          e.stopPropagation();
                          toggleAccordion(index);
                        }
                      }}
                      style={{ cursor: 'pointer', textDecoration: 'none' }}
                    >
                      <span className={expanded || isGroupActive ? 'fw-semibold' : ''}>
                        {group?.label || ''}
                      </span>
                    </a>
                  ) : (
                    <button
                      className={`accordion-button ${!hasChildren ? 'no-arrow' : ''} ${expanded ? '' : 'collapsed'}`}
                      type="button"
                      data-bs-toggle={hasChildren ? 'collapse' : undefined}
                      data-bs-target={hasChildren ? `#collapse-${groupId}` : undefined}
                      aria-controls={hasChildren ? `collapse-${groupId}` : undefined}
                      aria-expanded={expanded}
                      disabled={!hasChildren}
                      onClick={() => {
                        if (hasChildren) {
                          toggleAccordion(index);
                        }
                      }}
                    >
                      <span className={expanded ? 'fw-semibold' : ''}>
                        {group?.label || ''}
                      </span>
                    </button>
                  )}
                </h2>

                {hasChildren && (
                  <div
                    id={`collapse-${groupId}`}
                    className={`accordion-collapse collapse ${expanded ? 'show' : ''}`}
                    aria-labelledby={`heading-${groupId}`}
                    data-bs-parent={`#${uid}`}
                  >
                    <div className="accordion-body p-0">
                      <ul className="sidebar-category-list">
                        {children.map((child, childIdx) => {
                          const isActive = !!child?.href && child.href === activeHref;
                          const href = child?.href;
                          const hasValidHref = isValidHref(href);
                          const count = child?.count !== undefined ? child.count : '';

                          return (
                            <li
                              key={childIdx}
                              className={`sidebar-category-item ${isActive ? 'active' : ''}`}
                            >
                              {hasValidHref ? (
                                isInternalLink(href) ? (
                                  <a
                                    href={href}
                                    className="sidebar-category-link"
                                    onClick={(e) => handleLinkClick(e, href)}
                                  >
                                    <span className="category-name">{child?.label || ''}</span>
                                    {count !== '' && (
                                      <span className="category-count">{count}</span>
                                    )}
                                  </a>
                                ) : (
                                  <a href={href} className="sidebar-category-link">
                                    <span className="category-name">{child?.label || ''}</span>
                                    {count !== '' && (
                                      <span className="category-count">{count}</span>
                                    )}
                                  </a>
                                )
                              ) : (
                                <span className="sidebar-category-link">
                                  <span className="category-name">{child?.label || ''}</span>
                                  {count !== '' && (
                                    <span className="category-count">{count}</span>
                                  )}
                                </span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="sidebar-title-line"></div>
      </div>
    </div>
  );
};

export default SubSidebarMenu;

